import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { motion, useReducedMotion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { COMMON_NAMESPACE, WELCOME_NAMESPACE } from '@/i18n';
import { welcomeCopy } from '@/Pages/Welcome/copy';
import '@/components/WelcomeHeadline/WelcomeHeadline.sass';

const HEADLINE_MOTION = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] as const },
};

type Props = {
  document?: string;
};

function HeadlineContent({ document }: Props): JSX.Element {
  const { t } = useTranslation(WELCOME_NAMESPACE);

  return (
    <>
      <Box className="welcome-headline__label-block">
        <Typography component="p" className="welcome-headline__label">
          {welcomeCopy.label}
        </Typography>
        <Typography component="p" className="welcome-headline__subtitle">
          {welcomeCopy.subtitle}
        </Typography>
      </Box>

      <Box
        className="welcome-headline__badge"
        data-testid="welcome-headline-badge"
      >
        <Typography component="p" className="welcome-headline__badge-text">
          {welcomeCopy.badge}
        </Typography>
      </Box>

      <Box component="h1" className="welcome-headline__headline-group">
        <Typography
          component="span"
          className="welcome-headline__headline-line"
        >
          {welcomeCopy.headlineLine1}
        </Typography>
        <Typography
          component="span"
          className="welcome-headline__headline-line welcome-headline__headline-line--mark"
        >
          {welcomeCopy.headlineLine2}
        </Typography>
      </Box>

      <Typography component="p" className="welcome-headline__description">
        {welcomeCopy.description}
      </Typography>

      {document !== undefined && document !== '' && (
        <Box className="welcome-headline__document">
          <Typography component="p" className="welcome-headline__document-label">
            {t('userDocumentLabel')}
          </Typography>
          <Box className="welcome-headline__document-field">
            <Typography component="span" className="welcome-headline__document-value">
              {document}
            </Typography>
          </Box>
        </Box>
      )}
    </>
  );
}

export function WelcomeHeadline({ document }: Props): JSX.Element {
  const reduceMotion = useReducedMotion();
  const { t } = useTranslation(COMMON_NAMESPACE);
  const welcomeAriaLabel = t('welcomeAriaLabel');

  if (reduceMotion) {
    return (
      <Box
        component="section"
        className="welcome-headline"
        data-testid="welcome-headline"
        data-motion="reduced"
        aria-label={welcomeAriaLabel}
      >
        <HeadlineContent document={document} />
      </Box>
    );
  }

  return (
    <Box
      component={motion.section}
      className="welcome-headline"
      data-testid="welcome-headline"
      data-motion="animated"
      aria-label={welcomeAriaLabel}
      initial={HEADLINE_MOTION.initial}
      animate={HEADLINE_MOTION.animate}
      transition={HEADLINE_MOTION.transition}
    >
      <HeadlineContent document={document} />
    </Box>
  );
}
